import { useState, useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from '@/lib/api'
import { X, Search } from 'lucide-react'

interface SimpleUser {
  id: number
  full_name: string
  role: string
}

interface Props {
  label: string
  value: string
  onChange: (name: string) => void
  placeholder?: string
}

export default function UserSearchSelect({ label, value, onChange, placeholder }: Props) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const { data: users } = useQuery<SimpleUser[]>({
    queryKey: ['users-list'],
    queryFn: () => api.get('/users/list').then(r => r.data),
  })

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const filtered = (users || []).filter(u =>
    u.full_name.toLowerCase().includes(query.toLowerCase())
  )

  return (
    <div ref={ref} className="relative">
      <label className="text-sm font-medium text-slate-700 block mb-1">{label}</label>
      {value ? (
        <div className="flex items-center gap-2 w-full px-3 py-2 text-sm border rounded-lg bg-slate-50">
          <span className="flex-1 text-slate-900">{value}</span>
          <button type="button" onClick={() => { onChange(''); setQuery('') }} className="text-slate-400 hover:text-slate-600">
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
          <input
            type="text"
            value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true) }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder || 'Search by name...'}
            className="w-full pl-8 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
          />
        </div>
      )}
      {open && !value && (
        <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-slate-200 max-h-48 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-400">No users found</div>
          ) : (
            filtered.map(u => (
              <button key={u.id} type="button"
                onClick={() => { onChange(u.full_name); setQuery(''); setOpen(false) }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-red-50 flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-red-100 text-red-900 flex items-center justify-center text-xs font-medium shrink-0">
                  {u.full_name.charAt(0)}
                </div>
                <span className="flex-1">{u.full_name}</span>
                <span className="text-xs text-slate-400 capitalize">{u.role}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
